import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AnalyticsDashboard from '../components/analytics/AnalyticsDashboard';
import CoursesAnalytics from '../components/analytics/CoursesAnalytics';
import StudentsAnalytics from '../components/analytics/StudentsAnalytics';
import RevenueAnalytics from '../components/analytics/RevenueAnalytics';
import PerformanceAnalytics from '../components/analytics/PerformanceAnalytics';
import {
  BarChart3,
  Users,
  BookOpen,
  DollarSign,
  Activity,
  Calendar,
  Download,
  Filter
} from 'lucide-react';

const Analytics = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Vue d\'ensemble', icon: BarChart3 },
    { id: 'courses', name: 'Cours', icon: BookOpen },
    { id: 'students', name: 'Étudiants', icon: Users },
    { id: 'revenue', name: 'Revenus', icon: DollarSign },
    { id: 'performance', name: 'Performance', icon: Activity }
  ];

  if (!user) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès non autorisé</h1>
          <p className="text-gray-600">Vous devez être connecté pour accéder aux analytics.</p>
        </div>
      </div>
    );
  }

  // Vérifier si l'utilisateur est admin ou instructeur
  const isAuthorized = user.role === 'admin' || user.role === 'instructor';

  if (!isAuthorized) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès restreint</h1>
          <p className="text-gray-600 mb-6">
            Seuls les administrateurs et instructeurs peuvent accéder aux analytics.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-text">Analytics & Statistiques</h1>
              <p className="text-gray-600">
                Tableau de bord complet des performances de la plateforme
              </p>
            </div>
          </div>
        </div>

        {/* Navigation par onglets */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Contenu des onglets */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div>
              <AnalyticsDashboard />
            </div>
          )}

          {activeTab === 'courses' && (
            <div>
              <CoursesAnalytics />
            </div>
          )}

          {activeTab === 'students' && (
            <div>
              <StudentsAnalytics />
            </div>
          )}

          {activeTab === 'revenue' && (
            <div>
              <RevenueAnalytics />
            </div>
          )}

          {activeTab === 'performance' && (
            <div>
              <PerformanceAnalytics />
            </div>
          )}
        </div>

        {/* Informations supplémentaires */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Période</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Les données sont mises à jour en temps réel et couvrent les 30 derniers jours.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Export</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Toutes les données peuvent être exportées au format CSV pour analyse externe.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Filter className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Utilisez les filtres pour analyser des périodes spécifiques ou des segments d'utilisateurs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
